import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSettings,
  FiLock,
  FiUsers,
  FiGlobe,
  FiShield,
  FiUserPlus,
  FiEye,
  FiChevronRight,
  FiEdit3,
  FiImage,
  FiCamera,
  FiUpload,
  FiTrash2,
  FiPlus,
  FiSave,
  FiRefreshCw,
  FiX,
  FiStar,
  FiFileText,
  FiUserCheck,
  FiToggleLeft,
  FiToggleRight,
  FiAlertTriangle,
  FiCheck,
  FiBookOpen,
} from "react-icons/fi";
import { toast } from "react-toastify";
import communityService from "../../services/communityService";
import JoinRequestManagement from "./JoinRequestManagement";
import "./styles/index.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const slideVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
};

// Fields and subfields data
const fields = [
  "Computer Science",
  "Mathematics",
  "Physics",
  "Chemistry",
  "Biology",
  "Medicine",
  "Engineering",
  "Economics",
  "Psychology",
  "Sociology",
  "Philosophy",
  "Literature",
  "History",
  "Political Science",
  "Law",
  "Environmental Science",
  "Data Science",
  "Artificial Intelligence",
  "Neuroscience",
  "Materials Science",
  "Astronomy",
  "Geology",
  "Linguistics",
  "Anthropology",
  "Education",
  "Business Administration",
  "Finance",
  "Marketing",
  "Public Health",
  "Architecture",
  "Other",
];

const subFieldsMap = {
  "Computer Science": [
    "Machine Learning",
    "Cybersecurity",
    "Distributed Systems",
    "Computer Vision",
    "NLP",
    "Software Engineering",
    "Algorithms",
    "Database Systems",
    "HCI",
    "Computer Networks",
    "Other",
  ],
  Mathematics: [
    "Pure Mathematics",
    "Applied Mathematics",
    "Statistics",
    "Probability",
    "Number Theory",
    "Algebra",
    "Analysis",
    "Geometry",
    "Topology",
    "Combinatorics",
    "Other",
  ],
  Physics: [
    "Quantum Physics",
    "Astrophysics",
    "Condensed Matter",
    "Particle Physics",
    "Nuclear Physics",
    "Optics",
    "Thermodynamics",
    "Mechanics",
    "Electromagnetism",
    "Other",
  ],
  Chemistry: [
    "Organic Chemistry",
    "Inorganic Chemistry",
    "Physical Chemistry",
    "Analytical Chemistry",
    "Biochemistry",
    "Polymer Chemistry",
    "Medicinal Chemistry",
    "Other",
  ],
  Biology: [
    "Molecular Biology",
    "Genetics",
    "Ecology",
    "Microbiology",
    "Cell Biology",
    "Evolutionary Biology",
    "Biotechnology",
    "Marine Biology",
    "Botany",
    "Zoology",
    "Other",
  ],
  Medicine: [
    "Clinical Medicine",
    "Surgery",
    "Pediatrics",
    "Psychiatry",
    "Oncology",
    "Cardiology",
    "Neurology",
    "Dermatology",
    "Radiology",
    "Pharmacology",
    "Other",
  ],
  Engineering: [
    "Civil Engineering",
    "Mechanical Engineering",
    "Electrical Engineering",
    "Chemical Engineering",
    "Aerospace Engineering",
    "Biomedical Engineering",
    "Industrial Engineering",
    "Other",
  ],
  Economics: [
    "Microeconomics",
    "Macroeconomics",
    "Behavioral Economics",
    "Development Economics",
    "Econometrics",
    "International Economics",
    "Environmental Economics",
    "Other",
  ],
  Psychology: [
    "Clinical Psychology",
    "Cognitive Psychology",
    "Social Psychology",
    "Developmental Psychology",
    "Neuropsychology",
    "Industrial-Organizational Psychology",
    "Educational Psychology",
    "Other",
  ],
  "Artificial Intelligence": [
    "Deep Learning",
    "Reinforcement Learning",
    "Computer Vision",
    "NLP",
    "Robotics",
    "Expert Systems",
    "Neural Networks",
    "Knowledge Representation",
    "Other",
  ],
  "Data Science": [
    "Big Data",
    "Data Mining",
    "Predictive Analytics",
    "Data Visualization",
    "Statistical Modeling",
    "Business Intelligence",
    "Other",
  ],
  Other: [
    "Interdisciplinary Research",
    "Emerging Fields",
    "Cross-Disciplinary Studies",
    "Other",
  ],
};

// Add default subfields for fields not explicitly listed
Object.keys(fields).forEach((field) => {
  if (!subFieldsMap[field]) {
    subFieldsMap[field] = [
      "General",
      "Research Methods",
      "Theory",
      "Applications",
      "Other",
    ];
  }
});

// Settings sections configuration
const settingsSections = [
  {
    id: "access-control",
    label: "Access Control",
    description: "Manage visibility and permissions",
    icon: FiLock,
  },
  {
    id: "general",
    label: "General Settings",
    description: "Basic community information",
    icon: FiSettings,
  },
  {
    id: "guidelines",
    label: "Guidelines",
    description: "Community rules and policies",
    icon: FiFileText,
  },
  {
    id: "ratings",
    label: "Ratings",
    description: "View community feedback",
    icon: FiStar,
  },
  {
    id: "join-requests",
    label: "Join Requests",
    description: "Pending membership requests",
    icon: FiUserPlus,
  },
  {
    id: "moderation",
    label: "Moderation",
    description: "Content and member moderation",
    icon: FiShield,
  },
];

// Toggle component
const SettingToggle = ({
  label,
  description,
  checked,
  onChange,
  disabled,
  warning,
}) => (
  <motion.div
    className={`collab-comm-setting-toggle ${disabled ? "disabled" : ""}`}
    variants={itemVariants}
  >
    <div className="collab-comm-toggle-content">
      <div className="collab-comm-toggle-label">{label}</div>
      <div className="collab-comm-toggle-description">{description}</div>
      {warning && (
        <div className="collab-comm-toggle-warning">
          <FiAlertTriangle size={12} />
          <span>{warning}</span>
        </div>
      )}
    </div>
    <button
      className={`collab-comm-toggle-switch ${checked ? "active" : ""}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      aria-label={`Toggle ${label}`}
    >
      {checked ? <FiToggleRight size={24} /> : <FiToggleLeft size={24} />}
    </button>
  </motion.div>
);

// Input field component
const InputField = ({
  label,
  name,
  value,
  onChange,
  error,
  required,
  maxLength,
  children,
  placeholder,
}) => (
  <div className={`collab-comm-input-field ${error ? "has-error" : ""}`}>
    <label className="collab-comm-input-label">
      {label}
      {required && <span className="collab-comm-required">*</span>}
    </label>
    {children || (
      <input
        type="text"
        name={name}
        value={value}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="collab-comm-input"
      />
    )}
    {error && <span className="collab-comm-input-error">{error}</span>}
    {maxLength && (
      <span className="collab-comm-char-count">
        {value?.length || 0}/{maxLength}
      </span>
    )}
  </div>
);

const CommunitySettings = ({ communityId, community }) => {
  const [activeSection, setActiveSection] = useState("access-control");
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [generalHasChanges, setGeneralHasChanges] = useState(false);
  const [errors, setErrors] = useState({});

  // Access control settings state
  const [settings, setSettings] = useState({
    allowPublicViewing: false,
    allowPublicChatViewing: false,
    allowPublicDiscussionViewing: false,
    allowPublicMemberViewing: false,
    allowPublicRoomViewing: false,
    allowPublicRoomDetails: false,
    allowDirectJoin: false,
    requireApprovalForJoining: true,
    allowMemberRoomCreation: false,
  });

  // General settings state
  const [generalSettings, setGeneralSettings] = useState({
    name: "",
    description: "",
    field: "",
    subField: "",
    type: "public",
  });

  // Photo management state
  const [photoState, setPhotoState] = useState({
    hasPhoto: false,
    signedPhotoURL: null,
    selectedFile: null,
    previewURL: null,
    uploading: false,
  });

  // Guidelines state
  const [guidelines, setGuidelines] = useState([]);
  const [newGuideline, setNewGuideline] = useState({
    title: "",
    description: "",
  });
  const [editingGuideline, setEditingGuideline] = useState(null);
  const [guidelinesLoading, setGuidelinesLoading] = useState(false);

  // Ratings state
  const [allRatings, setAllRatings] = useState([]);
  const [ratingSummary, setRatingSummary] = useState(null);
  const [ratingsLoading, setRatingsLoading] = useState(false);

  // Initialize settings from community data
  useEffect(() => {
    if (community) {
      // Read settings from community.settings (per backend guide)
      const s = community.settings || {};
      setSettings({
        allowPublicViewing: s.allowPublicViewing ?? true,
        allowPublicChatViewing: s.allowPublicChatViewing ?? false,
        allowPublicDiscussionViewing: s.allowPublicDiscussionViewing ?? true,
        allowPublicMemberViewing: s.allowPublicMemberViewing ?? true,
        allowPublicRoomViewing: s.allowPublicRoomViewing ?? false,
        allowPublicRoomDetails: s.allowPublicRoomDetails ?? false,
        allowDirectJoin: s.allowDirectJoin ?? false,
        requireApprovalForJoining: s.requireApprovalForJoining ?? true,
        allowMemberRoomCreation: s.allowMemberRoomCreation ?? false,
      });

      setGeneralSettings({
        name: community.name || "",
        description: community.description || "",
        field: community.field || "",
        subField: Array.isArray(community.subField) 
          ? community.subField[0] || "" 
          : community.subField || "",
        type: community.type || "public",
      });

      // Photo: use signedPhotoURL directly from community object
      if (community.signedPhotoURL || community.photoURL || community.photo) {
        setPhotoState((prev) => ({
          ...prev,
          hasPhoto: true,
          signedPhotoURL: community.signedPhotoURL || null,
        }));
        // Only call API if signedPhotoURL not already present
        if (!community.signedPhotoURL && community.photo) {
          loadCommunityPhoto();
        }
      }
    }
  }, [community]);

  // Load data when section changes
  useEffect(() => {
    if (activeSection === "guidelines") {
      loadGuidelines();
    } else if (activeSection === "ratings") {
      loadRatings();
    }
  }, [activeSection, communityId]);

  const loadCommunityPhoto = async () => {
    try {
      const response = await communityService.getCommunityPhoto(communityId);
      if (response.signedUrl) {
        setPhotoState((prev) => ({
          ...prev,
          signedPhotoURL: response.signedUrl,
        }));
      }
    } catch (error) { /* ignored */ }
  };

  const loadGuidelines = async () => {
    setGuidelinesLoading(true);
    try {
      const response = await communityService.getCommunityGuidelines(communityId);
      // Response structure: { data: { guidelines: { guidelines: [...] } } }
      const guidelinesData = response.data?.guidelines?.guidelines || 
                             response.guidelines?.guidelines || 
                             response.guidelines || 
                             [];
      setGuidelines(guidelinesData);
    } catch (error) {
toast.error("Failed to load guidelines");
    } finally {
      setGuidelinesLoading(false);
    }
  };

  const loadRatings = async () => {
    setRatingsLoading(true);
    try {
      const [ratingsRes, summaryRes] = await Promise.all([
        communityService.getAllCommunityRatings(communityId),
        communityService.getCommunityRatingSummary(communityId),
      ]);
      // Response structure: { data: { recentRatings: [...] } }
      setAllRatings(ratingsRes.data?.recentRatings || ratingsRes.ratings || []);
      // Response structure: { data: { summary: {...} } }
      setRatingSummary(summaryRes.data?.summary || summaryRes.summary || summaryRes);
    } catch (error) { /* ignored */ } finally {
      setRatingsLoading(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);

    // Handle cascading disabled states
    if (key === "allowPublicViewing" && !value) {
      setSettings((prev) => ({
        ...prev,
        allowPublicChatViewing: false,
        allowPublicDiscussionViewing: false,
        allowPublicMemberViewing: false,
        allowPublicRoomViewing: false,
        allowPublicRoomDetails: false,
      }));
    }
    if (key === "allowPublicRoomViewing" && !value) {
      setSettings((prev) => ({
        ...prev,
        allowPublicRoomDetails: false,
      }));
    }
    if (key === "allowDirectJoin" && value) {
      setSettings((prev) => ({
        ...prev,
        requireApprovalForJoining: false,
      }));
    }
  };

  const handleGeneralSettingChange = (key, value) => {
    setGeneralSettings((prev) => ({ ...prev, [key]: value }));
    setGeneralHasChanges(true);

    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: null }));
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      // Backend expects: { settings: {...} } - NOT { accessControl: {...} }
      await communityService.updateCommunitySettings(communityId, { settings: settings });
      toast.success("Settings saved successfully");
      setHasChanges(false);
    } catch (error) {
      toast.error(error.message || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  const validateGeneralSettings = () => {
    const newErrors = {};
    if (!generalSettings.name?.trim()) {
      newErrors.name = "Community name is required";
    }
    if (!generalSettings.description?.trim()) {
      newErrors.description = "Description is required";
    }
    if (!generalSettings.field) {
      newErrors.field = "Please select a field";
    }
    if (!generalSettings.subField) {
      newErrors.subField = "Please select a sub-field";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveGeneralSettings = async () => {
    if (!validateGeneralSettings()) {
      toast.error("Please fix the validation errors");
      return;
    }

    setLoading(true);
    try {
      await communityService.updateCommunity(communityId, generalSettings);
      toast.success("General settings saved successfully");
      setGeneralHasChanges(false);
    } catch (error) {
      toast.error(error.message || "Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  // Photo management functions
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please select a valid image file (JPG, PNG, GIF, or WebP)");
      return;
    }

    const previewURL = URL.createObjectURL(file);
    setPhotoState((prev) => ({
      ...prev,
      selectedFile: file,
      previewURL,
    }));
  };

  const uploadPhoto = async () => {
    if (!photoState.selectedFile) return;

    setPhotoState((prev) => ({ ...prev, uploading: true }));
    try {
      const formData = new FormData();
      formData.append("photo", photoState.selectedFile);

      await communityService.uploadCommunityPhoto(communityId, formData);
      toast.success("Photo uploaded successfully");

      // Refresh photo
      await loadCommunityPhoto();
      setPhotoState((prev) => ({
        ...prev,
        hasPhoto: true,
        selectedFile: null,
        previewURL: null,
      }));
    } catch (error) {
      toast.error(error.message || "Failed to upload photo");
    } finally {
      setPhotoState((prev) => ({ ...prev, uploading: false }));
    }
  };

  const deletePhoto = async () => {
    if (
      !window.confirm("Are you sure you want to remove the community photo?")
    ) {
      return;
    }

    setLoading(true);
    try {
      await communityService.deleteCommunityPhoto(communityId);
      toast.success("Photo removed successfully");
      setPhotoState({
        hasPhoto: false,
        signedPhotoURL: null,
        selectedFile: null,
        previewURL: null,
        uploading: false,
      });
    } catch (error) {
      toast.error(error.message || "Failed to remove photo");
    } finally {
      setLoading(false);
    }
  };

  const cancelPhotoSelection = () => {
    if (photoState.previewURL) {
      URL.revokeObjectURL(photoState.previewURL);
    }
    setPhotoState((prev) => ({
      ...prev,
      selectedFile: null,
      previewURL: null,
    }));
  };

  // Guidelines management functions
  const addGuideline = async () => {
    if (!newGuideline.title.trim() || !newGuideline.description.trim()) {
      toast.error("Please fill in both title and description");
      return;
    }

    setGuidelinesLoading(true);
    try {
      await communityService.addCommunityGuideline(communityId, {
        title: newGuideline.title.trim(),
        description: newGuideline.description.trim(),
        order: guidelines.length + 1,
      });
      toast.success("Guideline added successfully");
      setNewGuideline({ title: "", description: "" });
      await loadGuidelines();
    } catch (error) {
      toast.error(error.message || "Failed to add guideline");
    } finally {
      setGuidelinesLoading(false);
    }
  };

  const updateGuidelines = async () => {
    setGuidelinesLoading(true);
    try {
      await communityService.updateCommunityGuidelines(communityId, guidelines);
      toast.success("Guidelines updated successfully");
      setEditingGuideline(null);
    } catch (error) {
      toast.error(error.message || "Failed to update guidelines");
    } finally {
      setGuidelinesLoading(false);
    }
  };

  const deleteGuideline = async (guidelineId) => {
    if (!window.confirm("Are you sure you want to delete this guideline?")) {
      return;
    }

    setGuidelinesLoading(true);
    try {
      await communityService.deleteCommunityGuideline(communityId, guidelineId);
      toast.success("Guideline deleted successfully");
      await loadGuidelines();
    } catch (error) {
      toast.error(error.message || "Failed to delete guideline");
    } finally {
      setGuidelinesLoading(false);
    }
  };

  // Render Access Control Settings
  const renderAccessControlSettings = () => (
    <motion.div
      className="collab-comm-settings-section"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className="collab-comm-section-header"
        variants={itemVariants}
      >
        <div className="collab-comm-section-icon">
          <FiLock size={24} />
        </div>
        <div className="collab-comm-section-info">
          <h3>Access Control</h3>
          <p>Manage who can view and interact with your community</p>
        </div>
      </motion.div>

      {/* Public Viewing Controls - Only for Public Communities */}
      {community?.type === "public" && (
        <motion.div
          className="collab-comm-setting-group"
          variants={itemVariants}
        >
          <div className="collab-comm-group-header">
            <FiEye size={16} />
            <h4>Public Viewing Options</h4>
          </div>
          <p className="collab-comm-group-description">
            Allow non-members to view community content in read-only mode
          </p>

          <SettingToggle
            label="Allow general public viewing"
            description="Enable basic community information to be visible to everyone"
            checked={settings.allowPublicViewing}
            onChange={(value) =>
              handleSettingChange("allowPublicViewing", value)
            }
          />

          <SettingToggle
            label="Allow outsiders to view chat"
            description="Non-members can read chat messages but cannot send messages"
            checked={settings.allowPublicChatViewing}
            onChange={(value) =>
              handleSettingChange("allowPublicChatViewing", value)
            }
            disabled={!settings.allowPublicViewing}
          />

          <SettingToggle
            label="Allow outsiders to view discussions"
            description="Non-members can read threads and replies but cannot create or reply"
            checked={settings.allowPublicDiscussionViewing}
            onChange={(value) =>
              handleSettingChange("allowPublicDiscussionViewing", value)
            }
            disabled={!settings.allowPublicViewing}
          />

          <SettingToggle
            label="Allow outsiders to view members"
            description="Non-members can see the member list with limited information"
            checked={settings.allowPublicMemberViewing}
            onChange={(value) =>
              handleSettingChange("allowPublicMemberViewing", value)
            }
            disabled={!settings.allowPublicViewing}
          />
        </motion.div>
      )}

      {/* Research Room Access Controls */}
      {community?.type === "public" && (
        <motion.div
          className="collab-comm-setting-group"
          variants={itemVariants}
        >
          <div className="collab-comm-group-header">
            <FiBookOpen size={16} />
            <h4>Research Room Access</h4>
          </div>
          <p className="collab-comm-group-description">
            Control how outsiders can view your community's research activities
          </p>

          <SettingToggle
            label="Allow public to view research room list"
            description="Outsiders can see what research areas your community is working on"
            checked={settings.allowPublicRoomViewing}
            onChange={(value) =>
              handleSettingChange("allowPublicRoomViewing", value)
            }
            disabled={!settings.allowPublicViewing}
            warning={
              !settings.allowPublicViewing
                ? "Disabled because general public viewing is off"
                : null
            }
          />

          <SettingToggle
            label="Allow public to view research room details"
            description="Outsiders can view room descriptions and topics (member lists remain private)"
            checked={settings.allowPublicRoomDetails}
            onChange={(value) =>
              handleSettingChange("allowPublicRoomDetails", value)
            }
            disabled={!settings.allowPublicRoomViewing}
            warning={
              !settings.allowPublicRoomViewing
                ? "Disabled because room viewing is off"
                : null
            }
          />
        </motion.div>
      )}

      {/* Join Method Controls */}
      <motion.div className="collab-comm-setting-group" variants={itemVariants}>
        <div className="collab-comm-group-header">
          <FiUserCheck size={16} />
          <h4>Join Method</h4>
        </div>
        <p className="collab-comm-group-description">
          Configure how users can join your community
        </p>

        {community?.type === "public" && (
          <SettingToggle
            label="Allow direct join"
            description="Users can join instantly without approval"
            checked={settings.allowDirectJoin}
            onChange={(value) => handleSettingChange("allowDirectJoin", value)}
            warning={
              settings.allowDirectJoin
                ? "Users will join immediately without moderator review"
                : null
            }
          />
        )}

        <SettingToggle
          label="Require approval for joining"
          description="Join requests need moderator approval"
          checked={settings.requireApprovalForJoining}
          onChange={(value) =>
            handleSettingChange("requireApprovalForJoining", value)
          }
          disabled={settings.allowDirectJoin}
          warning={
            settings.allowDirectJoin
              ? "Disabled because direct join is enabled"
              : null
          }
        />
      </motion.div>

      {/* Member Permissions */}
      <motion.div className="collab-comm-setting-group" variants={itemVariants}>
        <div className="collab-comm-group-header">
          <FiUsers size={16} />
          <h4>Member Permissions</h4>
        </div>
        <p className="collab-comm-group-description">
          Control what community members can do
        </p>

        <SettingToggle
          label="Allow members to create rooms"
          description="Regular members can create research rooms"
          checked={settings.allowMemberRoomCreation}
          onChange={(value) =>
            handleSettingChange("allowMemberRoomCreation", value)
          }
        />
      </motion.div>

      {/* Save Button */}
      <motion.div
        className="collab-comm-settings-actions"
        variants={itemVariants}
      >
        <motion.button
          onClick={saveSettings}
          disabled={!hasChanges || loading}
          className={`collab-comm-save-btn ${hasChanges ? "has-changes" : ""}`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? (
            <>
              <FiRefreshCw className="collab-comm-spinning" size={16} />
              Saving...
            </>
          ) : (
            <>
              <FiSave size={16} />
              {hasChanges ? "Save Changes" : "All Changes Saved"}
            </>
          )}
        </motion.button>
        {hasChanges && (
          <motion.p
            className="collab-comm-unsaved-notice"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FiAlertTriangle size={14} />
            You have unsaved changes
          </motion.p>
        )}
      </motion.div>
    </motion.div>
  );

  // Render General Settings
  const renderGeneralSettings = () => (
    <motion.div
      className="collab-comm-settings-section"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className="collab-comm-section-header"
        variants={itemVariants}
      >
        <div className="collab-comm-section-icon">
          <FiSettings size={24} />
        </div>
        <div className="collab-comm-section-info">
          <h3>General Settings</h3>
          <p>Manage basic community information and configuration</p>
        </div>
      </motion.div>

      {/* Basic Information */}
      <motion.div className="collab-comm-setting-group" variants={itemVariants}>
        <div className="collab-comm-group-header">
          <FiEdit3 size={16} />
          <h4>Basic Information</h4>
        </div>
        <p className="collab-comm-group-description">
          Update your community's core details and identity
        </p>

        <div className="collab-comm-settings-form">
          <InputField
            label="Community Name"
            name="name"
            value={generalSettings.name}
            onChange={handleGeneralSettingChange}
            placeholder="Enter community name..."
            error={errors.name}
            required={true}
            maxLength={100}
          />

          <div
            className={`collab-comm-input-field ${
              errors.description ? "has-error" : ""
            }`}
          >
            <label className="collab-comm-input-label">
              Description
              <span className="collab-comm-required">*</span>
            </label>
            <textarea
              name="description"
              value={generalSettings.description}
              onChange={(e) =>
                handleGeneralSettingChange("description", e.target.value)
              }
              placeholder="Describe what your community is about..."
              className="collab-comm-textarea"
              maxLength={500}
              rows={4}
            />
            {errors.description && (
              <span className="collab-comm-input-error">
                {errors.description}
              </span>
            )}
            <span className="collab-comm-char-count">
              {generalSettings.description?.length || 0}/500
            </span>
          </div>

          <div
            className={`collab-comm-input-field ${
              errors.field ? "has-error" : ""
            }`}
          >
            <label className="collab-comm-input-label">
              Field
              <span className="collab-comm-required">*</span>
            </label>
            <select
              name="field"
              value={generalSettings.field}
              onChange={(e) => {
                handleGeneralSettingChange("field", e.target.value);
                handleGeneralSettingChange("subField", "");
              }}
              className="collab-comm-select"
            >
              <option value="">Select a field...</option>
              {fields.map((field) => (
                <option key={field} value={field}>
                  {field}
                </option>
              ))}
            </select>
            {errors.field && (
              <span className="collab-comm-input-error">{errors.field}</span>
            )}
          </div>

          <div
            className={`collab-comm-input-field ${
              errors.subField ? "has-error" : ""
            }`}
          >
            <label className="collab-comm-input-label">
              Sub-field
              <span className="collab-comm-required">*</span>
            </label>
            <select
              name="subField"
              value={generalSettings.subField}
              onChange={(e) =>
                handleGeneralSettingChange("subField", e.target.value)
              }
              className="collab-comm-select"
              disabled={!generalSettings.field}
            >
              <option value="">Select a sub-field...</option>
              {generalSettings.field &&
                subFieldsMap[generalSettings.field]?.map((subField) => (
                  <option key={subField} value={subField}>
                    {subField}
                  </option>
                ))}
            </select>
            {errors.subField && (
              <span className="collab-comm-input-error">{errors.subField}</span>
            )}
          </div>

          <div className="collab-comm-input-field">
            <label className="collab-comm-input-label">Community Type</label>
            <div className="collab-comm-type-selector">
              <motion.label
                className={`collab-comm-type-option ${
                  generalSettings.type === "public" ? "selected" : ""
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <input
                  type="radio"
                  name="type"
                  value="public"
                  checked={generalSettings.type === "public"}
                  onChange={(e) =>
                    handleGeneralSettingChange("type", e.target.value)
                  }
                />
                <div className="collab-comm-type-content">
                  <FiGlobe size={20} />
                  <div>
                    <div className="collab-comm-type-title">Public</div>
                    <div className="collab-comm-type-desc">
                      Anyone can discover and request to join
                    </div>
                  </div>
                </div>
              </motion.label>

              <motion.label
                className={`collab-comm-type-option ${
                  generalSettings.type === "private" ? "selected" : ""
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <input
                  type="radio"
                  name="type"
                  value="private"
                  checked={generalSettings.type === "private"}
                  onChange={(e) =>
                    handleGeneralSettingChange("type", e.target.value)
                  }
                />
                <div className="collab-comm-type-content">
                  <FiLock size={20} />
                  <div>
                    <div className="collab-comm-type-title">Private</div>
                    <div className="collab-comm-type-desc">
                      Only invited users can join
                    </div>
                  </div>
                </div>
              </motion.label>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Photo Management */}
      <motion.div className="collab-comm-setting-group" variants={itemVariants}>
        <div className="collab-comm-group-header">
          <FiImage size={16} />
          <h4>Community Photo</h4>
        </div>
        <p className="collab-comm-group-description">
          Upload and manage your community's profile photo
        </p>

        <div className="collab-comm-photo-management">
          <div className="collab-comm-photo-preview">
            {photoState.previewURL ? (
              <div className="collab-comm-photo-container preview">
                <img
                  src={photoState.previewURL}
                  alt="New community photo preview"
                  className="collab-comm-photo-image"
                />
                <div className="collab-comm-photo-overlay">
                  <div className="collab-comm-photo-actions">
                    <motion.button
                      type="button"
                      onClick={uploadPhoto}
                      disabled={photoState.uploading}
                      className="collab-comm-photo-btn primary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {photoState.uploading ? (
                        <>
                          <FiRefreshCw
                            className="collab-comm-spinning"
                            size={16}
                          />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FiUpload size={16} />
                          Upload Photo
                        </>
                      )}
                    </motion.button>
                    <motion.button
                      type="button"
                      onClick={cancelPhotoSelection}
                      className="collab-comm-photo-btn secondary"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FiX size={16} />
                      Cancel
                    </motion.button>
                  </div>
                </div>
              </div>
            ) : photoState.hasPhoto && photoState.signedPhotoURL ? (
              <div className="collab-comm-photo-container">
                <img
                  src={photoState.signedPhotoURL}
                  alt="Current community photo"
                  className="collab-comm-photo-image"
                />
                <div className="collab-comm-photo-overlay">
                  <div className="collab-comm-photo-actions">
                    <label className="collab-comm-photo-btn primary">
                      <FiCamera size={16} />
                      Change Photo
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleFileSelect}
                        style={{ display: "none" }}
                      />
                    </label>
                    <motion.button
                      type="button"
                      onClick={deletePhoto}
                      className="collab-comm-photo-btn danger"
                      disabled={loading}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FiTrash2 size={16} />
                      Remove
                    </motion.button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="collab-comm-photo-placeholder">
                <FiCamera size={40} />
                <p>No photo uploaded</p>
                <label className="collab-comm-photo-btn primary">
                  <FiUpload size={16} />
                  Upload Photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
            )}
          </div>

          <div className="collab-comm-photo-requirements">
            <h5>Photo Requirements:</h5>
            <ul>
              <li>Maximum file size: 5MB</li>
              <li>Supported formats: JPG, PNG, GIF, WebP</li>
              <li>Recommended size: 400x400 pixels</li>
            </ul>
          </div>
        </div>
      </motion.div>

      {/* Save Button */}
      <motion.div
        className="collab-comm-settings-actions"
        variants={itemVariants}
      >
        <motion.button
          onClick={saveGeneralSettings}
          disabled={!generalHasChanges || loading}
          className={`collab-comm-save-btn ${
            generalHasChanges ? "has-changes" : ""
          }`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          {loading ? (
            <>
              <FiRefreshCw className="collab-comm-spinning" size={16} />
              Saving...
            </>
          ) : (
            <>
              <FiSave size={16} />
              {generalHasChanges ? "Save Changes" : "All Changes Saved"}
            </>
          )}
        </motion.button>
        {generalHasChanges && (
          <motion.p
            className="collab-comm-unsaved-notice"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <FiAlertTriangle size={14} />
            You have unsaved changes
          </motion.p>
        )}
      </motion.div>
    </motion.div>
  );

  // Render Guidelines Settings
  const renderGuidelinesSettings = () => (
    <motion.div
      className="collab-comm-guidelines-section"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div
        className="collab-comm-guidelines-header"
        variants={itemVariants}
      >
        <div className="collab-comm-guidelines-icon">
          <FiFileText size={32} />
        </div>
        <div className="collab-comm-guidelines-title">
          <h2>Community Guidelines</h2>
          <p>
            Create and manage rules that help maintain a positive community
            environment
          </p>
        </div>
      </motion.div>

      <div className="collab-comm-guidelines-content">
        {/* Add New Guideline Card */}
        <motion.div
          className="collab-comm-add-guideline-card"
          variants={itemVariants}
        >
          <div className="collab-comm-guideline-card-header">
            <FiPlus size={20} />
            <h3>Add New Guideline</h3>
          </div>

          <div className="collab-comm-guideline-form">
            <div className="collab-comm-form-field">
              <label>Title *</label>
              <input
                type="text"
                placeholder="e.g., 'Be respectful and professional'"
                value={newGuideline.title}
                onChange={(e) =>
                  setNewGuideline({ ...newGuideline, title: e.target.value })
                }
                maxLength={100}
              />
              <span className="collab-comm-char-count">
                {newGuideline.title.length}/100
              </span>
            </div>

            <div className="collab-comm-form-field">
              <label>Description *</label>
              <textarea
                placeholder="Provide a clear explanation of this guideline..."
                value={newGuideline.description}
                onChange={(e) =>
                  setNewGuideline({
                    ...newGuideline,
                    description: e.target.value,
                  })
                }
                rows="3"
                maxLength={500}
              />
              <span className="collab-comm-char-count">
                {newGuideline.description.length}/500
              </span>
            </div>

            <motion.button
              onClick={addGuideline}
              className="collab-comm-add-guideline-btn"
              disabled={
                !newGuideline.title.trim() ||
                !newGuideline.description.trim() ||
                guidelinesLoading
              }
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <FiPlus size={16} />
              Add Guideline
            </motion.button>
          </div>
        </motion.div>

        {/* Existing Guidelines */}
        <motion.div
          className="collab-comm-existing-guidelines"
          variants={itemVariants}
        >
          <div className="collab-comm-guidelines-list-header">
            <h3>Current Guidelines ({guidelines.length})</h3>
            <motion.button
              onClick={loadGuidelines}
              className="collab-comm-refresh-btn"
              disabled={guidelinesLoading}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FiRefreshCw
                size={16}
                className={guidelinesLoading ? "collab-comm-spinning" : ""}
              />
              Refresh
            </motion.button>
          </div>

          {guidelinesLoading ? (
            <div className="collab-comm-loading-state">
              <FiRefreshCw size={24} className="collab-comm-spinning" />
              <span>Loading guidelines...</span>
            </div>
          ) : guidelines.length > 0 ? (
            <motion.div
              className="collab-comm-guidelines-list"
              variants={containerVariants}
            >
              {guidelines
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((guideline, index) => (
                  <motion.div
                    key={guideline._id || index}
                    className="collab-comm-guideline-item"
                    variants={itemVariants}
                    layout
                  >
                    <div className="collab-comm-guideline-number">
                      {guideline.order || index + 1}
                    </div>

                    <div className="collab-comm-guideline-body">
                      {editingGuideline === guideline._id ? (
                        <div className="collab-comm-guideline-edit-form">
                          <input
                            type="text"
                            value={guideline.title}
                            onChange={(e) => {
                              const updatedGuidelines = guidelines.map((g) =>
                                g._id === guideline._id
                                  ? { ...g, title: e.target.value }
                                  : g
                              );
                              setGuidelines(updatedGuidelines);
                            }}
                            className="collab-comm-edit-input"
                          />
                          <textarea
                            value={guideline.description}
                            onChange={(e) => {
                              const updatedGuidelines = guidelines.map((g) =>
                                g._id === guideline._id
                                  ? { ...g, description: e.target.value }
                                  : g
                              );
                              setGuidelines(updatedGuidelines);
                            }}
                            rows="3"
                            className="collab-comm-edit-textarea"
                          />
                          <div className="collab-comm-edit-actions">
                            <motion.button
                              onClick={updateGuidelines}
                              className="collab-comm-save-edit-btn"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              <FiCheck size={14} />
                              Save
                            </motion.button>
                            <motion.button
                              onClick={() => setEditingGuideline(null)}
                              className="collab-comm-cancel-edit-btn"
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                            >
                              Cancel
                            </motion.button>
                          </div>
                        </div>
                      ) : (
                        <div className="collab-comm-guideline-display">
                          <h4>{guideline.title}</h4>
                          <p>{guideline.description}</p>
                        </div>
                      )}
                    </div>

                    <div className="collab-comm-guideline-actions">
                      <motion.button
                        onClick={() =>
                          setEditingGuideline(
                            editingGuideline === guideline._id
                              ? null
                              : guideline._id
                          )
                        }
                        className="collab-comm-guideline-action-btn edit"
                        title="Edit guideline"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiEdit3 size={16} />
                      </motion.button>
                      <motion.button
                        onClick={() => deleteGuideline(guideline._id)}
                        className="collab-comm-guideline-action-btn delete"
                        title="Delete guideline"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <FiTrash2 size={16} />
                      </motion.button>
                    </div>
                  </motion.div>
                ))}
            </motion.div>
          ) : (
            <motion.div
              className="collab-comm-empty-guidelines"
              variants={itemVariants}
            >
              <div className="collab-comm-empty-icon">
                <FiFileText size={64} />
              </div>
              <h3>No Guidelines Yet</h3>
              <p>Create your first community guideline using the form above.</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );

  // Render Rating Settings
  const renderRatingSettings = () => (
    <motion.div
      className="collab-comm-settings-section"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className="collab-comm-section-header"
        variants={itemVariants}
      >
        <div className="collab-comm-section-icon">
          <FiStar size={24} />
        </div>
        <div className="collab-comm-section-info">
          <h3>Rating Management</h3>
          <p>View and manage community ratings and feedback</p>
        </div>
      </motion.div>

      {ratingsLoading ? (
        <div className="collab-comm-loading-state">
          <FiRefreshCw size={24} className="collab-comm-spinning" />
          <span>Loading ratings...</span>
        </div>
      ) : (
        <>
          {/* Rating Summary */}
          {ratingSummary && (
            <motion.div
              className="collab-comm-rating-summary"
              variants={itemVariants}
            >
              <h4>Rating Summary</h4>
              <div className="collab-comm-rating-overview">
                <div className="collab-comm-rating-score">
                  <span className="collab-comm-score-number">
                    {ratingSummary.averageOverall?.toFixed(1) || "0.0"}
                  </span>
                  <div className="collab-comm-rating-stars">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <FiStar
                        key={star}
                        size={18}
                        className={
                          star <= Math.round(ratingSummary.averageOverall || 0)
                            ? "filled"
                            : "empty"
                        }
                      />
                    ))}
                  </div>
                  <span className="collab-comm-rating-count">
                    ({ratingSummary.totalRatings || 0} reviews)
                  </span>
                </div>

                <div className="collab-comm-rating-breakdown">
                  <div className="collab-comm-rating-metric">
                    <span className="collab-comm-metric-label">
                      Discussion Quality
                    </span>
                    <span className="collab-comm-metric-value">
                      {ratingSummary.averageDiscussionQuality?.toFixed(1) ||
                        "0.0"}
                    </span>
                  </div>
                  <div className="collab-comm-rating-metric">
                    <span className="collab-comm-metric-label">
                      Helpfulness
                    </span>
                    <span className="collab-comm-metric-value">
                      {ratingSummary.averageHelpfulness?.toFixed(1) || "0.0"}
                    </span>
                  </div>
                  <div className="collab-comm-rating-metric">
                    <span className="collab-comm-metric-label">
                      Knowledge Sharing
                    </span>
                    <span className="collab-comm-metric-value">
                      {ratingSummary.averageKnowledgeSharing?.toFixed(1) ||
                        "0.0"}
                    </span>
                  </div>
                  <div className="collab-comm-rating-metric">
                    <span className="collab-comm-metric-label">
                      Community Support
                    </span>
                    <span className="collab-comm-metric-value">
                      {ratingSummary.averageCommunitySupport?.toFixed(1) ||
                        "0.0"}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Individual Ratings */}
          <motion.div
            className="collab-comm-ratings-list"
            variants={itemVariants}
          >
            <h4>Individual Ratings ({allRatings.length})</h4>
            {allRatings.length > 0 ? (
              <div className="collab-comm-ratings-grid">
                {allRatings.map((rating, index) => (
                  <motion.div
                    key={rating._id || index}
                    className="collab-comm-rating-card"
                    variants={itemVariants}
                    whileHover={{ y: -2 }}
                  >
                    <div className="collab-comm-rating-card-header">
                      <div className="collab-comm-rating-user-info">
                        <span className="collab-comm-rating-user-name">
                          {rating.isAnonymous
                            ? "Anonymous User"
                            : rating.user?.name || "User"}
                        </span>
                        <span className="collab-comm-rating-date">
                          {new Date(rating.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="collab-comm-rating-overall">
                        <FiStar size={16} className="filled" />
                        <span>{rating.ratings.overall}</span>
                      </div>
                    </div>

                    <div className="collab-comm-rating-details">
                      <div className="collab-comm-rating-breakdown-small">
                        <span>DQ: {rating.ratings.discussionQuality}</span>
                        <span>H: {rating.ratings.helpfulness}</span>
                        <span>KS: {rating.ratings.knowledgeSharing}</span>
                        <span>CS: {rating.ratings.communitySupport}</span>
                      </div>
                    </div>

                    {rating.feedback && (
                      <div className="collab-comm-rating-feedback">
                        <p>"{rating.feedback}"</p>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            ) : (
              <motion.div
                className="collab-comm-empty-ratings"
                variants={itemVariants}
              >
                <FiStar size={48} />
                <h4>No Ratings Yet</h4>
                <p>
                  Community ratings will appear here once members submit
                  feedback.
                </p>
              </motion.div>
            )}
          </motion.div>
        </>
      )}
    </motion.div>
  );

  // Render content based on active section
  const renderContent = () => {
    switch (activeSection) {
      case "access-control":
        return renderAccessControlSettings();
      case "general":
        return renderGeneralSettings();
      case "guidelines":
        return renderGuidelinesSettings();
      case "ratings":
        return renderRatingSettings();
      case "join-requests":
        return (
          <JoinRequestManagement
            communityId={communityId}
            community={community}
          />
        );
      case "moderation":
        return (
          <motion.div
            className="collab-comm-settings-placeholder"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              className="collab-comm-placeholder-icon"
              variants={itemVariants}
            >
              <FiShield size={64} />
            </motion.div>
            <motion.h3 variants={itemVariants}>Moderation Tools</motion.h3>
            <motion.p variants={itemVariants}>
              Advanced moderation features and content policies will be
              available here soon.
            </motion.p>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="collab-comm-settings">
      {/* Header */}
      <motion.div
        className="collab-comm-settings-header"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="collab-comm-settings-header-icon">
          <FiSettings size={28} />
        </div>
        <div className="collab-comm-settings-header-info">
          <h2>Community Settings</h2>
          <p>Manage your community preferences and access control</p>
        </div>
      </motion.div>

      {/* Main Layout */}
      <div className="collab-comm-settings-layout">
        {/* Sidebar Navigation */}
        <motion.div
          className="collab-comm-settings-sidebar"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          {settingsSections.map((section, index) => (
            <motion.button
              key={section.id}
              className={`collab-comm-settings-nav-item ${
                activeSection === section.id ? "active" : ""
              }`}
              onClick={() => setActiveSection(section.id)}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
              whileHover={{ x: 4 }}
            >
              <section.icon size={20} />
              <div className="collab-comm-nav-item-content">
                <span className="collab-comm-nav-item-label">
                  {section.label}
                </span>
                <span className="collab-comm-nav-item-description">
                  {section.description}
                </span>
              </div>
              <FiChevronRight size={16} className="collab-comm-nav-arrow" />
            </motion.button>
          ))}
        </motion.div>

        {/* Content Area */}
        <motion.div
          className="collab-comm-settings-content"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeSection}
              variants={slideVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default CommunitySettings;
